import React from 'react';
import { motion } from 'framer-motion';
import { Eye, Brain, Zap, Heart, ArrowRight } from 'lucide-react';

const funnelStages = [
  {
    icon: Eye,
    name: 'Awareness',
    description: 'Capture attention through strategic content and targeted advertising',
    color: 'cyan',
    bgColor: 'bg-cyan-500/10',
    textColor: 'text-cyan-400',
    borderColor: 'border-cyan-500/30'
  },
  {
    icon: Brain,
    name: 'Consideration',
    description: 'Nurture interest with valuable content and lead magnets',
    color: 'blue',
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-400',
    borderColor: 'border-blue-500/30'
  },
  {
    icon: Zap,
    name: 'Conversion',
    description: 'Convert prospects with compelling offers and seamless checkout',
    color: 'purple',
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-400',
    borderColor: 'border-purple-500/30'
  },
  {
    icon: Heart,
    name: 'Loyalty',
    description: 'Retain customers and turn them into advocates who refer others',
    color: 'pink',
    bgColor: 'bg-pink-500/10',
    textColor: 'text-pink-400',
    borderColor: 'border-pink-500/30'
  },
];

export default function SolutionSection() {
  return (
    <section 
      id="solution"
      data-testid="solution-section"
      className="section-padding relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#030303]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,240,255,0.05)_0%,_transparent_70%)]" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4 block">
            The Solution
          </span>
          <h2 
            data-testid="solution-headline"
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            A Structured
            <br />
            <span className="gradient-text-cyan">Growth Engine</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-base md:text-lg">
            A systematic marketing funnel that guides prospects from first touch 
            to loyal customer - and back again.
          </p>
        </motion.div>
        
        {/* Funnel Visualization */}
        <div className="max-w-4xl mx-auto">
          {/* Desktop Funnel */}
          <div className="hidden md:block relative">
            {/* Connection Line */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 transform -translate-y-1/2 rounded-full opacity-30" />
            
            <div className="relative grid grid-cols-4 gap-4">
              {funnelStages.map((stage, index) => (
                <motion.div
                  key={index}
                  data-testid={`funnel-stage-${index}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.6 }}
                  className="relative"
                >
                  {/* Stage Card */}
                  <div className={`glass rounded-2xl p-6 text-center card-hover border ${stage.borderColor}`}>
                    {/* Icon */}
                    <div className={`w-14 h-14 mx-auto rounded-xl ${stage.bgColor} flex items-center justify-center mb-4`}>
                      <stage.icon className={`w-7 h-7 ${stage.textColor}`} />
                    </div>
                    
                    {/* Name */}
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {stage.name}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-gray-400 text-xs leading-relaxed">
                      {stage.description}
                    </p>
                  </div>
                  
                  {/* Arrow */}
                  {index < funnelStages.length - 1 && (
                    <div className="absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <ArrowRight className={`w-6 h-6 ${stage.textColor}`} />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
            
            {/* Loop Back Arrow */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
              className="absolute -bottom-12 left-1/2 transform -translate-x-1/2"
            >
              <svg width="400" height="50" viewBox="0 0 400 50" fill="none" className="opacity-30">
                <path 
                  d="M380 10 C380 40 20 40 20 10" 
                  stroke="url(#loopGradient)" 
                  strokeWidth="2" 
                  strokeDasharray="4 4"
                  fill="none"
                />
                <defs>
                  <linearGradient id="loopGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#ec4899" />
                    <stop offset="100%" stopColor="#00F0FF" />
                  </linearGradient>
                </defs>
              </svg>
              <p className="text-xs text-gray-500 text-center mt-2">Referrals create new leads</p>
            </motion.div>
          </div>
          
          {/* Mobile Funnel */}
          <div className="md:hidden space-y-4">
            {funnelStages.map((stage, index) => (
              <motion.div
                key={index}
                data-testid={`funnel-stage-mobile-${index}`}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`glass rounded-2xl p-6 flex items-center gap-4 border ${stage.borderColor}`}
              >
                <div className={`w-12 h-12 rounded-xl ${stage.bgColor} flex items-center justify-center flex-shrink-0`}>
                  <stage.icon className={`w-6 h-6 ${stage.textColor}`} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white mb-1">
                    {stage.name}
                  </h3>
                  <p className="text-gray-400 text-xs leading-relaxed">
                    {stage.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Key Insight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mt-24 text-center"
        >
          <div className="inline-block glass-heavy rounded-2xl p-8 max-w-2xl">
            <p className="text-gray-300 text-lg leading-relaxed">
              <span className="text-cyan-400 font-semibold">&quot;The funnel isn&apos;t just a process&quot;</span> - 
              it&apos;s a living system where every customer interaction feeds back into growth.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
